import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Images } from "../../../../../../../utils/imageConstants";
import type { IMatchDetails } from "../types/matchDetails.d";
import type { IMatchSummary } from "../../types/IMatches";
import { QUERY_KEY } from "../../../../../../../services/queryEndPoint";
import { Api } from "../../../../../../../services/interceptor";
import { getMatchEquationText, resolvePublicImageUrl } from "../../../../../../../helper/helper";

interface UpcomingMatchRecentPerformanceProps {
  matchSummary?: IMatchDetails;
}

type MatchResult = "W" | "L" | "N/R";

const getTeamResult = (match: IMatchSummary, teamName: string): MatchResult => {
  const result = (match.matchResult ?? "").trim().toLowerCase();
  if (!result || result === "-") return "N/R";
  if (result.includes("no result") || result.includes("abandoned") || result.includes("tie")) {
    return "N/R";
  }
  return result.includes(teamName.toLowerCase()) ? "W" : "L";
};

const getResultClass = (result: MatchResult) => {
  if (result === "W") return "result-win";
  if (result === "L") return "result-loss";
  return "result-nr";
};

const UpcomingMatchRecentPerformance: React.FC<UpcomingMatchRecentPerformanceProps> = ({ matchSummary }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const teamOneSnapshotId = matchSummary?.teamOne?.teamSnapshotId;
  const teamTwoSnapshotId = matchSummary?.teamTwo?.teamSnapshotId;
  const teamOneName = matchSummary?.teamOne?.teamName ?? "Team 1";
  const teamTwoName = matchSummary?.teamTwo?.teamName ?? "Team 2";
  const teamOneImage = resolvePublicImageUrl(matchSummary?.teamOne?.imageUrl) || Images.default_team_image;
  const teamTwoImage = resolvePublicImageUrl(matchSummary?.teamTwo?.imageUrl) || Images.default_team_image;

  const { data: teamOneMatches = [] } = useQuery<IMatchSummary[]>({
    queryKey: [QUERY_KEY.matchByTeamAndStatus, teamOneSnapshotId, "recent"],
    enabled: !!teamOneSnapshotId,
    queryFn: async () => {
      const [endedRes, abandonedRes] = await Promise.all([
        Api.get(QUERY_KEY.matchByTeamAndStatus, { params: { teamId: teamOneSnapshotId, status: "ENDED", page: 0, size: 5 } }).catch(() => null),
        Api.get(QUERY_KEY.matchByTeamAndStatus, { params: { teamId: teamOneSnapshotId, status: "ABANDONED", page: 0, size: 5 } }).catch(() => null)
      ]);
      const ended = endedRes?.data?.matchResponseList ?? [];
      const abandoned = abandonedRes?.data?.matchResponseList ?? [];
      return [...ended, ...abandoned]
        .sort((a, b) => new Date(b.startDate || b.dateTime || 0).getTime() - new Date(a.startDate || a.dateTime || 0).getTime())
        .slice(0, 5);
    },
  });

  const { data: teamTwoMatches = [] } = useQuery<IMatchSummary[]>({
    queryKey: [QUERY_KEY.matchByTeamAndStatus, teamTwoSnapshotId, "recent"],
    enabled: !!teamTwoSnapshotId,
    queryFn: async () => {
      const [endedRes, abandonedRes] = await Promise.all([
        Api.get(QUERY_KEY.matchByTeamAndStatus, { params: { teamId: teamTwoSnapshotId, status: "ENDED", page: 0, size: 5 } }).catch(() => null),
        Api.get(QUERY_KEY.matchByTeamAndStatus, { params: { teamId: teamTwoSnapshotId, status: "ABANDONED", page: 0, size: 5 } }).catch(() => null)
      ]);
      const ended = endedRes?.data?.matchResponseList ?? [];
      const abandoned = abandonedRes?.data?.matchResponseList ?? [];
      return [...ended, ...abandoned]
        .sort((a, b) => new Date(b.startDate || b.dateTime || 0).getTime() - new Date(a.startDate || a.dateTime || 0).getTime())
        .slice(0, 5);
    },
  });

  const teamOneResults: MatchResult[] = teamOneMatches
    .slice(0, 5)
    .map((m) => getTeamResult(m, teamOneName));

  const teamTwoResults: MatchResult[] = teamTwoMatches
    .slice(0, 5)
    .map((m) => getTeamResult(m, teamTwoName));

  const renderResultIcons = (results: MatchResult[]) => (
    <div className="result-icons">
      {results.map((result, idx) => {
        const isLast = idx === results.length - 1;
        return (
          <div key={idx} className="result-icon-wrapper">
            <div className={`result-icon ${getResultClass(result)} ${isLast ? "has-tooltip" : ""}`}>
              <span>{result}</span>
              {isLast && (
                <div className="result-icon-tooltip">
                  <div className="tooltip-arrow"></div>
                  <p>Most Recent Match</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {results.length === 0 && (
        <span className="no-recent-matches">No recent matches</span>
      )}
    </div>
  );

  return (
    <div className="upcoming-recent-performance-container">
      <div className="upcoming-recent-performance-header">
        <p>Recent Perform</p>
        <p>Last 5 Matches</p>
      </div>
      <div className="upcoming-recent-performance-content">
        <div className="upcoming-recent-performance-teams">
          <div className="upcoming-recent-performance-team-list">
            {[
              { name: teamOneName, image: teamOneImage },
              { name: teamTwoName, image: teamTwoImage }
            ].map((team, index) => (
              <div key={index} className="upcoming-recent-performance-team-item">
                <ol start={index + 1}>
                  <li>
                    <div className="team-info">
                      <p>{team.name.toUpperCase()}</p>
                    </div>
                  </li>
                </ol>
              </div>
            ))}
          </div>
          <div className="upcoming-recent-performance-results">
            <div className="upcoming-recent-performance-result-row">
              {renderResultIcons(teamOneResults)}
              <img
                src={Images.dropdown_icon}
                alt="dropdown"
                className={`dropdown-icon ${isDropdownOpen ? "open" : ""}`}
                onClick={() => setIsDropdownOpen((v) => !v)}
              />
            </div>
            <div className="upcoming-recent-performance-result-row">
              {renderResultIcons(teamTwoResults)}
              <img
                src={Images.dropdown_icon}
                alt="dropdown"
                className={`dropdown-icon ${isDropdownOpen ? "open" : ""}`}
                onClick={() => setIsDropdownOpen((v) => !v)}
              />
            </div>
          </div>
        </div>
        {isDropdownOpen && (
          <div className="upcoming-recent-performance-details">
            {teamOneMatches.slice(0, 5).map((match, index) => {
              const result = getTeamResult(match, teamOneName);
              return (
                <div key={index} className="upcoming-recent-performance-detail-item">
                  <div className="detail-left">
                    <div className={`result-icon ${getResultClass(result)}`}>
                      <span>{result}</span>
                    </div>
                    <div className="match-info">
                      <p className="match-title">
                        {match.teamOne?.teamName} Vs {match.teamTwo?.teamName}
                      </p>
                      <p className="match-result">{getMatchEquationText(match)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingMatchRecentPerformance;
